import React from "react"
import { C, radius, shadow } from "@/theme/newDesignTokens"

// ── Static styles ────────────────────────────────────────────────────────────

export const ils: Record<string, React.CSSProperties> = {
    // Header bar
    headerBar: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0 0 8px",
    },
    statsText: {
        flex: 1,
        fontSize: 11,
        color: C.medGray,
        letterSpacing: 0.3,
    },
    viewToggleWrapper: {
        display: "flex",
        borderRadius: 9999,
        overflow: "hidden",
        border: `1px solid ${C.lightGray}`,
    },
    sortWrapper: {
        position: "relative",
        flexShrink: 0,
    },
    sortMenu: {
        position: "absolute",
        top: "calc(100% + 4px)",
        right: 0,
        background: C.white,
        border: `1px solid ${C.lightGray}`,
        borderRadius: radius.md,
        boxShadow: shadow.md,
        zIndex: 50,
        minWidth: 160,
        padding: "4px 0",
    },
    bulkDeleteButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        verticalAlign: "middle",
        padding: "3px 5px",
        marginLeft: 4,
        background: `${C.coral}15`,
        border: `1px solid ${C.coral}40`,
        borderRadius: radius.md,
        cursor: "pointer",
        color: C.coral,
    },

    // List view
    listContainer: {
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    listScrollable: {
        flex: 1,
        overflow: "auto",
    },
    rowWrapper: {
        position: "relative",
        borderBottom: `1px solid ${C.lightGray}`,
    },
    rowCheckbox: {
        flexShrink: 0,
        width: 20,
        paddingTop: 2,
    },
    rowContentArea: {
        flex: 1,
        minWidth: 0,
        cursor: "pointer",
    },

    // Loading / empty
    loadingCenter: {
        display: "flex",
        justifyContent: "center",
        padding: 40,
    },
    emptyState: {
        textAlign: "center",
        padding: 40,
        color: C.medGray,
        fontSize: 13,
    },

    // Pagination
    paginationRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "16px 0 8px",
    },
    paginationText: {
        fontSize: 12,
        color: C.medGray,
    },
    pageSizeWrapper: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        marginRight: 8,
    },
    pageSizeLabel: {
        fontSize: 11,
        color: C.medGray,
    },
    pageSizeSelect: {
        fontSize: 11,
        color: C.medGray,
        padding: "4px 6px",
        border: `1px solid ${C.lightGray}`,
        borderRadius: radius.md,
        background: C.white,
        cursor: "pointer",
        fontFamily: "inherit",
    },
}

// ── Factory functions ────────────────────────────────────────────────────────

export const getViewToggleButton = (active: boolean): React.CSSProperties => ({
    padding: "5px 8px",
    background: active ? `${C.navy}15` : "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
})

export const getSortButton = (open: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 10px",
    background: open ? C.offWhite : "transparent",
    border: `1px solid ${open ? C.lightGray : "transparent"}`,
    borderRadius: radius.md,
    cursor: "pointer",
    fontSize: 11,
    color: C.medGray,
    fontFamily: "inherit",
    transition: "all 0.15s",
})

export const getSortChevron = (open: boolean): React.CSSProperties => ({
    opacity: 0.5,
    transition: "transform 0.15s",
    transform: open ? "rotate(180deg)" : "none",
})

export const getSortMenuItem = (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    textAlign: "left",
    padding: "7px 12px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 12,
    color: active ? C.navyDark : C.medGray,
    fontWeight: active ? 600 : 400,
    fontFamily: "inherit",
    transition: "background 0.1s",
})

export const getRowContent = (isSelected: boolean, isHovered: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "13px 4px",
    background: isSelected ? `${C.navy}08` : isHovered ? `${C.navy}06` : "transparent",
    transition: "background 0.15s",
    borderRadius: 4,
})

export const getRowIconBox = (color: string): React.CSSProperties => ({
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: radius.sm,
    background: `${color}20`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
})

export const getRowMenuWrapper = (visible: boolean): React.CSSProperties => ({
    flexShrink: 0,
    opacity: visible ? 1 : 0,
    transition: "opacity 0.15s",
    position: "relative",
})

export const getRowMenuButton = (isActive: boolean): React.CSSProperties => ({
    width: 26,
    height: 26,
    border: "none",
    background: isActive ? C.offWhite : "transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.medGray,
})

export const getPaginationButton = (disabled: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    border: `1px solid ${C.lightGray}`,
    borderRadius: radius.md,
    background: C.white,
    cursor: disabled ? "not-allowed" : "pointer",
    color: disabled ? C.medGray : C.navyDark,
})
